import { useEffect, useState } from 'react';

import {
  presenceChangeEventSchema,
  presenceSnapshotEventSchema,
  type PresenceViewer,
} from '@emr/shared';

/**
 * Live "who else is viewing this chart" state for one patient, over
 * `GET /api/patients/:patientId/live` (`server/src/clinical/presenceRoutes.ts`, `BACKLOG.md` C11).
 *
 * Uses the browser's native `EventSource` rather than a hand-rolled `fetch` reader. That is a
 * deliberate choice, not the default reached for out of convenience: per the SSE spec, an
 * `EventSource` whose *initial* response is not `200 text/event-stream` (this route's
 * `chart_access_denied`/`not_found`/`forbidden`/`unauthenticated`) fails the connection
 * permanently — `readyState` goes straight to `CLOSED` with no automatic retry — while a connection
 * that *was* established and then drops (a real network blip) retries on its own with backoff.
 * Those are exactly the two behaviors this hook wants, and the spec gives them for free; a
 * hand-rolled reader would have to reimplement both, and get the "permanent vs. transient" split
 * right, by hand.
 *
 * `unavailable` distinguishes "the connection failed for good" from "still loading" — a caller
 * shows the presence indicator once viewers are non-empty and otherwise renders nothing, so
 * `unavailable` alone rarely needs its own UI, but it is surfaced rather than swallowed in case a
 * caller wants to know (e.g. to avoid implying real-time freshness once the stream has given up).
 *
 * **If a caller ever renders this hook across a change in `patientId` without unmounting**, give
 * the consuming element `key={patientId}` at the call site. This hook does not reset its own state
 * to `EMPTY_STATE` when `patientId` changes — `setState` synchronously at the top of an effect body
 * is the exact anti-pattern `eslint-plugin-react-hooks`'s `set-state-in-effect` rule flags (it forces
 * an extra render pass on every mount, not just on a real `patientId` change) — so a full remount via
 * `key` is the correct way to get a clean initial state for a new patient, the same as any other
 * component whose state should not carry over across an identity change.
 */
export interface ChartPresenceState {
  readonly viewers: readonly PresenceViewer[];
  readonly unavailable: boolean;
}

const EMPTY_STATE: ChartPresenceState = { viewers: [], unavailable: false };

function parseJson(data: string): unknown {
  try {
    return JSON.parse(data) as unknown;
  } catch {
    return undefined;
  }
}

export function useChartPresence(patientId: string): ChartPresenceState {
  const [state, setState] = useState<ChartPresenceState>(EMPTY_STATE);

  useEffect(() => {
    const source = new EventSource(`/api/patients/${patientId}/live`, { withCredentials: true });

    // A named SSE event (anything but the default `message`) is not in `EventSourceEventMap`, so
    // `addEventListener`'s overload resolution falls back to a loosely-typed listener — the object
    // the browser actually dispatches is still a real `MessageEvent` with a string `.data`, per the
    // SSE spec, so this names that explicitly rather than leaving `event` under-typed.
    source.addEventListener('snapshot', (event: MessageEvent<string>) => {
      const parsed = presenceSnapshotEventSchema.safeParse(parseJson(event.data));
      if (parsed.success) setState({ viewers: parsed.data.viewers, unavailable: false });
    });

    source.addEventListener('join', (event: MessageEvent<string>) => {
      const parsed = presenceChangeEventSchema.safeParse(parseJson(event.data));
      if (!parsed.success) return;
      const viewer = parsed.data;
      setState((prev) => {
        if (prev.viewers.some((v) => v.connectionId === viewer.connectionId)) return prev;
        return { ...prev, viewers: [...prev.viewers, viewer] };
      });
    });

    source.addEventListener('leave', (event: MessageEvent<string>) => {
      const parsed = presenceChangeEventSchema.safeParse(parseJson(event.data));
      if (!parsed.success) return;
      const { connectionId } = parsed.data;
      setState((prev) => ({
        ...prev,
        viewers: prev.viewers.filter((v) => v.connectionId !== connectionId),
      }));
    });

    source.onerror = () => {
      // `CLOSED` means the spec has already given up reconnecting (see this hook's own header) —
      // any other `readyState` here is mid-retry after a transient drop, nothing to reflect yet.
      if (source.readyState === EventSource.CLOSED) {
        setState((prev) => ({ ...prev, unavailable: true }));
      }
    };

    return () => {
      source.close();
    };
  }, [patientId]);

  return state;
}
